import { Component, EventEmitter, OnDestroy, OnInit, Output, ViewChild } from '@angular/core';
import { FormControl, FormGroup, Validators } from '@angular/forms';
import { BehaviorSubject, Observable, startWith, Subject, switchMap, takeUntil } from 'rxjs';
import { EmailSignatureEditorComponent } from 'src/app/modules/campaign/modules/text-editor/components/email-signature-editor';
import {
  CampaignEmailCredential,
  EmailEncryptionProtocol,
} from 'src/app/shared/models/campaign/campaign-account';
import {
  domainName,
  emailPattern,
  notSpacePattern,
  starndartPasswordRegExp,
  trackingDomainName,
} from 'src/app/shared/utilities';

type AccordionType = 'account' | 'limit' | 'signature' | 'trachingDomain';

type EmailAccountSettingsFormData = {
  SMPTSettingsGroup: {
    fromName: string;
    fromEmail: string;
    useDiffAccountName: boolean;
    alternativeUsername?: string;
    appPassword: string;
    host: string;
    port: number;
    smtpEncriptionProtocol: EmailEncryptionProtocol;
    useDiffReplayAddress: boolean;
    alternativeReplayAddress?: string;
  };
  IMAPSettingsGroup: {
    receiveResponceOnDiffEmail: boolean;
    imapUserName?: string;
    imapPassword?: string;
    host: string;
    port: number;
    imapEncryptionProtocol: EmailEncryptionProtocol;
  };
  sendingLimitGroup: {
    delayType: 'fixed' | 'random';
    messagesPerDay: number;
    delay?: number;
    randomDelayFrom?: number;
    randomDelayTo?: number;
  };
  domainTrackingNameControl: string;
};

@Component({
  selector: 'app-email-account-settings',
  templateUrl: './email-account-settings.component.html',
  styleUrls: ['./email-account-settings.component.scss'],
  standalone: false,
})
export class EmailAccountSettingsComponent implements OnInit, OnDestroy {
  @ViewChild('signatureEditor') signatureEditor: EmailSignatureEditorComponent;

  @Output() validStateUpdate = new EventEmitter<boolean>();

  currentlyOpenedAccordion: AccordionType = 'account';

  SMPTSettingsGroup = new FormGroup({
    fromName: new FormControl<string>('', [
      Validators.required,
      Validators.pattern(notSpacePattern),
      Validators.maxLength(50),
    ]),
    fromEmail: new FormControl<string>('', [Validators.pattern(emailPattern), Validators.required]),
    useDiffAccountName: new FormControl<boolean>(false),
    alternativeUsername: new FormControl<string>('', Validators.maxLength(50)),
    appPassword: new FormControl<string>('', [
      Validators.pattern(starndartPasswordRegExp),
      Validators.required,
    ]),
    host: new FormControl<string>('smtp.gmail.com', [
      Validators.required,
      Validators.pattern(domainName),
    ]),
    port: new FormControl<number>(465, [
      Validators.min(1),
      Validators.max(65535),
      Validators.required,
    ]),
    smtpEncriptionProtocol: new FormControl<EmailEncryptionProtocol>('SSL'),
    useDiffReplayAddress: new FormControl<boolean>(false),
    alternativeReplayAddress: new FormControl<string>(''),
  });

  IMAPSettingsGroup = new FormGroup({
    receiveResponceOnDiffEmail: new FormControl<boolean>(false),
    imapUserName: new FormControl<string>(''),
    imapPassword: new FormControl<string>(''),
    host: new FormControl<string>('imap.gmail.com', [
      Validators.required,
      Validators.pattern(domainName),
    ]),
    port: new FormControl<number>(993, [
      Validators.min(1),
      Validators.max(65535),
      Validators.required,
    ]),
    imapEncryptionProtocol: new FormControl<EmailEncryptionProtocol>('SSL'),
  });

  sendingLimitGroup = new FormGroup({
    delayType: new FormControl<'fixed' | 'random'>('fixed'),
    messagesPerDay: new FormControl<number>(10),
    delay: new FormControl<number>(5),
    randomDelayFrom: new FormControl<number>(5),
    randomDelayTo: new FormControl<number>(3600),
  });

  domainTrackingNameControl = new FormControl<string>('', Validators.pattern(trackingDomainName));

  formGroup = new FormGroup({
    SMPTSettingsGroup: this.SMPTSettingsGroup,
    IMAPSettingsGroup: this.IMAPSettingsGroup,
    sendingLimitGroup: this.sendingLimitGroup,
    domainTrackingNameControl: this.domainTrackingNameControl,
  });

  private $unsubscribe = new Subject<void>();
  private reevaluateDisabledControlsSubject = new BehaviorSubject<null>(null);
  private signatureSubject$ = new BehaviorSubject<string | null>(null);

  signature$: Observable<string | null> = this.signatureSubject$.asObservable();

  ngOnInit(): void {
    this.handleLimitsForm();
    this.handleFormValidation();
    this.handleOptionalControlsValidations();
    this.handleDynamicDefaults();
    this.preventWhitespaces();
  }

  openAccordion(type: AccordionType): void {
    this.currentlyOpenedAccordion = type;
  }

  setSettings(settings?: Partial<CampaignEmailCredential>): void {
    if (!settings) return;

    const formData: EmailAccountSettingsFormData = {
      SMPTSettingsGroup: {
        fromName: settings.fromName,
        fromEmail: settings.fromEmail,
        useDiffAccountName: !!settings.username,
        appPassword: settings.password,
        host: settings.smtpHost,
        port: settings.smtpPort,
        smtpEncriptionProtocol: settings.smtpEncryptionProtocol,
        useDiffReplayAddress: !!settings.replyTo,
      },
      IMAPSettingsGroup: {
        receiveResponceOnDiffEmail: !!settings.imapUsername,
        imapPassword: settings.imapPassword,
        host: settings.imapHost,
        port: settings.imapPort,
        imapEncryptionProtocol: settings.imapEncryptionProtocol,
      },
      sendingLimitGroup: {
        delayType: !!settings.delay ? 'fixed' : 'random',
        messagesPerDay: settings.dailyLimit,
      },
      domainTrackingNameControl: settings.trackingDomain || '',
    };

    if (settings.username) {
      formData.SMPTSettingsGroup.alternativeUsername = settings.username;
    }
    if (settings.replyTo) {
      formData.SMPTSettingsGroup.alternativeReplayAddress = settings.replyTo;
    }
    if (settings.imapUsername) {
      formData.IMAPSettingsGroup.imapUserName = settings.imapUsername;
    }

    if (settings.delay) {
      formData.sendingLimitGroup.delay = settings.delay;
    } else {
      formData.sendingLimitGroup.randomDelayFrom = settings.randomDelayFrom;
      formData.sendingLimitGroup.randomDelayTo = settings.randomDelayTo;
    }
    if (settings.signature) {
      this.signatureSubject$.next(settings.signature);
    }

    this.formGroup.patchValue(formData, { emitEvent: false });
  }

  disableForm(): void {
    this.formGroup.disable({ emitEvent: false });
  }

  enableForm(): void {
    this.formGroup.enable({ emitEvent: false });
    this.reevaluateDisabledControlsSubject.next(null);
  }

  getSettings(): CampaignEmailCredential {
    const formData = this.formGroup.value as EmailAccountSettingsFormData;
    const sendingLimitGroup = this.sendingLimitGroup.value;

    let settings: CampaignEmailCredential = {
      username: formData.SMPTSettingsGroup.useDiffAccountName
        ? formData.SMPTSettingsGroup.alternativeUsername
        : null,
      imapUsername: formData.IMAPSettingsGroup.receiveResponceOnDiffEmail
        ? formData.IMAPSettingsGroup.imapUserName
        : null,
      imapPassword: formData.IMAPSettingsGroup.receiveResponceOnDiffEmail
        ? formData.IMAPSettingsGroup.imapPassword
        : null,
      fromName: formData.SMPTSettingsGroup.fromName,
      fromEmail: formData.SMPTSettingsGroup.fromEmail,
      replyTo: formData.SMPTSettingsGroup.useDiffReplayAddress
        ? formData.SMPTSettingsGroup.alternativeReplayAddress
        : null,
      imapHost: formData.IMAPSettingsGroup.host,
      imapPort: formData.IMAPSettingsGroup.port,
      smtpHost: formData.SMPTSettingsGroup.host,
      smtpPort: formData.SMPTSettingsGroup.port,
      dailyLimit: sendingLimitGroup.messagesPerDay,
      signature: this.signatureEditor.getContentAsHTML(),
      delay: sendingLimitGroup.delayType === 'fixed' ? sendingLimitGroup.delay : null,
      randomDelayFrom:
        sendingLimitGroup.delayType === 'random' ? sendingLimitGroup.randomDelayFrom : null,
      randomDelayTo:
        sendingLimitGroup.delayType === 'random' ? sendingLimitGroup.randomDelayTo : null,
      smtpEncryptionProtocol: formData.SMPTSettingsGroup.smtpEncriptionProtocol,
      imapEncryptionProtocol: formData.IMAPSettingsGroup.imapEncryptionProtocol,
      trackingDomain: formData.domainTrackingNameControl,
      password: formData.SMPTSettingsGroup.appPassword,
      privateAccess: null,
      smtpAuth: null,
    };

    settings = Object.fromEntries(
      Object.entries(settings).filter(
        ([_, value]) => value !== null && value !== undefined && value !== '',
      ),
    ) as CampaignEmailCredential;

    return settings;
  }

  private handleLimitsForm(): void {
    const delayType = this.sendingLimitGroup.get('delayType') as FormControl<'fixed' | 'random'>;
    const delay = this.sendingLimitGroup.get('delay') as FormControl<number>;
    const randomDelayFrom = this.sendingLimitGroup.get('randomDelayFrom') as FormControl<number>;
    const randomDelayTo = this.sendingLimitGroup.get('randomDelayTo') as FormControl<number>;

    this.reevaluateDisabledControlsSubject
      .pipe(
        takeUntil(this.$unsubscribe),
        switchMap(() => {
          return delayType.valueChanges.pipe(startWith(delayType.value));
        }),
      )
      .subscribe((type) => {
        if (type === 'fixed') {
          delay.enable();
          randomDelayFrom.disable();
          randomDelayTo.disable();
        } else {
          delay.disable();
          randomDelayFrom.enable();
          randomDelayTo.enable();
        }
      });

    // prevent delayTo from being smaller then delay From
    randomDelayFrom.valueChanges
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((delayFrom: number) => {
        if (delayFrom > randomDelayTo.value) {
          randomDelayTo.setValue(delayFrom);
        }
      });

    // Prevent delayFrom from being bigger then delayTo
    randomDelayTo.valueChanges.pipe(takeUntil(this.$unsubscribe)).subscribe((delayTo: number) => {
      if (delayTo < randomDelayFrom.value) {
        randomDelayFrom.setValue(delayTo);
      }
    });
  }

  private handleOptionalControlsValidations(): void {
    const useDiffAccountName = this.SMPTSettingsGroup.get(
      'useDiffAccountName',
    ) as FormControl<boolean>;
    const alternativeUsername = this.SMPTSettingsGroup.get(
      'alternativeUsername',
    ) as FormControl<string>;

    useDiffAccountName.valueChanges
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((useDiffAccountName: boolean) => {
        if (useDiffAccountName) {
          alternativeUsername.addValidators([
            Validators.pattern(notSpacePattern),
            Validators.required,
          ]);
        } else {
          alternativeUsername.clearValidators();
        }
        alternativeUsername.updateValueAndValidity();
      });

    const useDiffReplayAddress = this.SMPTSettingsGroup.get(
      'useDiffReplayAddress',
    ) as FormControl<boolean>;
    const alternativeReplayAddress = this.SMPTSettingsGroup.get(
      'alternativeReplayAddress',
    ) as FormControl<string>;

    useDiffReplayAddress.valueChanges
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((useDiffReplayAddress: boolean) => {
        if (useDiffReplayAddress) {
          alternativeReplayAddress.addValidators([
            Validators.pattern(emailPattern),
            Validators.required,
          ]);
        } else {
          alternativeReplayAddress.clearValidators();
        }
        alternativeReplayAddress.updateValueAndValidity();
      });

    const receiveResponceOnDiffEmail = this.IMAPSettingsGroup.get(
      'receiveResponceOnDiffEmail',
    ) as FormControl<boolean>;
    const imapUserName = this.IMAPSettingsGroup.get('imapUserName') as FormControl<string>;
    const imapPassword = this.IMAPSettingsGroup.get('imapPassword') as FormControl<string>;

    receiveResponceOnDiffEmail.valueChanges
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((receiveResponceOnDiffEmail: boolean) => {
        if (receiveResponceOnDiffEmail) {
          imapUserName.addValidators([Validators.pattern(notSpacePattern), Validators.required]);
          imapPassword.addValidators([
            Validators.pattern(starndartPasswordRegExp),
            Validators.required,
          ]);
        } else {
          imapUserName.clearValidators();
          imapPassword.clearValidators();
        }
        imapUserName.updateValueAndValidity();
        imapPassword.updateValueAndValidity();
      });
  }

  private handleFormValidation(): void {
    this.formGroup.valueChanges.pipe(takeUntil(this.$unsubscribe)).subscribe(() => {
      this.validStateUpdate.emit(this.formGroup.valid);
    });
  }

  private handleDynamicDefaults(): void {
    const smtpPortdefaults: Map<EmailEncryptionProtocol, number> = new Map([
      ['TLS', 587],
      ['SSL', 465],
      ['NONE', 25],
    ]);
    const imapPortdefaults: Map<EmailEncryptionProtocol, number> = new Map([
      ['TLS', 993],
      ['SSL', 993],
      ['NONE', 143],
    ]);

    const smtpEncryptionProtocol = this.SMPTSettingsGroup.get(
      'smtpEncriptionProtocol',
    ) as FormControl<EmailEncryptionProtocol>;
    const smtpPort = this.SMPTSettingsGroup.get('port') as FormControl<number>;

    smtpEncryptionProtocol.valueChanges
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((protocol: EmailEncryptionProtocol) => {
        smtpPort.setValue(smtpPortdefaults.get(protocol));
      });

    const imapEncryptionProtocol = this.IMAPSettingsGroup.get(
      'imapEncryptionProtocol',
    ) as FormControl<EmailEncryptionProtocol>;
    const imapPort = this.IMAPSettingsGroup.get('port') as FormControl<number>;

    imapEncryptionProtocol.valueChanges
      .pipe(takeUntil(this.$unsubscribe))
      .subscribe((protocol: EmailEncryptionProtocol) => {
        imapPort.setValue(imapPortdefaults.get(protocol));
      });
  }

  private preventWhitespaces(): void {
    this.enforceNoSpacesOnController(this.SMPTSettingsGroup.get('fromEmail') as FormControl);
    this.enforceNoSpacesOnController(this.SMPTSettingsGroup.get('host') as FormControl);
    this.enforceNoSpacesOnController(
      this.SMPTSettingsGroup.get('alternativeReplayAddress') as FormControl,
    );

    this.enforceNoSpacesOnController(this.IMAPSettingsGroup.get('host') as FormControl);
  }

  private enforceNoSpacesOnController(control: FormControl): void {
    control.valueChanges.pipe(takeUntil(this.$unsubscribe)).subscribe((value: any) => {
      if (typeof value === 'string' && value?.trim() !== value) {
        control.setValue(value.trim(), { emitEvent: false });
      }
    });
  }

  ngOnDestroy(): void {
    this.$unsubscribe.next();
    this.$unsubscribe.complete();
  }
}
